import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, ExternalLink, CheckCircle2, AlertTriangle, FileText, Phone } from "lucide-react";

import { SiteLayout } from "@/components/site/SiteLayout";
import { PageSEO } from "@/components/site/PageSEO";
import { ResourceHero } from "@/components/site/ResourceHero";
import { CTASection } from "@/components/site/CTASection";
import { Reveal, RevealStagger, RevealItem } from "@/components/motion/Reveal";
import { brand } from "@/data/site/brand";
import { irsForms, getForm } from "@/data/site/irsForms";
import { getFormDetail } from "@/data/site/irsFormDetails";

const IRSFormDetail = () => {
  const { slug = "" } = useParams();
  const form = getForm(slug);
  if (!form) return <Navigate to="/resources/irs-forms" replace />;

  const detail = getFormDetail(slug);
  const path = `/resources/irs-forms/${slug}`;
  const title = `IRS ${form.number} (${form.title}) — Plain-Language Guide`;
  const description =
    detail?.shortPurpose ?? `${form.purpose} ${form.whoFiles}`.slice(0, 158);

  const schemas: object[] = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: `IRS ${form.number}: ${form.title}`,
      description,
      url: `${brand.siteUrl}${path}`,
      mainEntityOfPage: `${brand.siteUrl}${path}`,
      author: { "@type": "Organization", name: brand.legalName, url: brand.siteUrl },
      publisher: { "@type": "Organization", name: brand.legalName, url: brand.siteUrl },
      about: `IRS ${form.number}`,
    },
  ];

  if (detail) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: `How to file IRS ${form.number}`,
      description: detail.shortPurpose,
      step: detail.steps.map((s, i) => ({
        "@type": "HowToStep",
        position: i + 1,
        name: s.title,
        text: s.body,
      })),
    });
    schemas.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: detail.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }

  return (
    <SiteLayout>
      <PageSEO
        title={title}
        description={description}
        path={path}
        image={form.image}
        ogType="article"
        breadcrumbs={[
          { name: "Home", href: "/" },
          { name: "Resources", href: "/resources" },
          { name: "IRS Forms", href: "/resources/irs-forms" },
          { name: form.number, href: path },
        ]}
        jsonLd={schemas}
      />

      <ResourceHero
        breadcrumbs={[
          { name: "Home", href: "/" },
          { name: "Resources", href: "/resources" },
          { name: "IRS Forms", href: "/resources/irs-forms" },
          { name: form.number },
        ]}
        eyebrow={`IRS ${form.number}`}
        title={form.title}
        summary={detail?.shortPurpose ?? form.purpose}
        facts={[
          { label: "Form", value: form.number },
          { label: "Who files", value: form.whoFiles.split(/[,.]/)[0] },
          { label: "Last reviewed", value: "2025" },
        ]}
        background="signingForm"
      />

      <section className="py-12 lg:py-16 bg-white">
        <div className="container mx-auto px-4 max-w-5xl grid lg:grid-cols-[220px_1fr] gap-10">
          {/* Form preview */}
          <div className="order-first">
            {form.image && (
              <a
                href={form.pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block group"
                aria-label={`Open official IRS ${form.number} PDF`}
              >
                <img
                  src={form.image}
                  alt={`First page of IRS ${form.number} — ${form.title}`}
                  loading="lazy"
                  width={220}
                  height={285}
                  className="w-full h-auto border border-border shadow-sm group-hover:shadow-md transition-shadow bg-white"
                />
              </a>
            )}
            {form.pdfUrl && (
              <a
                href={form.pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-navy hover:text-gold"
              >
                Official IRS PDF <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
            <dl className="mt-6 space-y-4 text-[13px]">
              <Row label="Purpose" value={form.purpose} />
              <Row label="Who files" value={form.whoFiles} />
              <Row label="When used" value={form.whenUsed} />
            </dl>
            {form.relatedServices && form.relatedServices.length > 0 && (
              <div className="mt-6 pt-6 border-t border-border">
                <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-2">
                  Related services
                </p>
                <ul className="space-y-1.5">
                  {form.relatedServices.map((r) => (
                    <li key={r.href}>
                      <Link to={r.href} className="text-sm font-semibold text-navy hover:text-gold inline-flex items-center gap-1">
                        {r.label} <ArrowRight className="w-3 h-3" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Body */}
          <div className="min-w-0">
            {detail ? (
              <>
                <Reveal>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy mb-5 leading-tight">
                    What {form.number} actually does
                  </h2>
                  <div className="space-y-4 text-[17px] text-foreground/85 leading-[1.75]">
                    {detail.overview.map((p, i) => (
                      <p key={i}>{p}</p>
                    ))}
                  </div>
                </Reveal>

                <Reveal>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy mt-12 mb-5 leading-tight">
                    How to file {form.number}, step by step
                  </h2>
                  <ol className="space-y-5">
                    {detail.steps.map((s, i) => (
                      <li key={s.title} className="grid grid-cols-[auto_1fr] gap-4">
                        <span className="font-heading text-2xl font-bold text-gold leading-none w-10 pt-0.5">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <div>
                          <h3 className="font-heading font-bold text-navy text-lg mb-1.5">{s.title}</h3>
                          <p className="text-foreground/85 leading-relaxed">{s.body}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </Reveal>

                <Reveal>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy mt-12 mb-5 leading-tight flex items-center gap-3">
                    <AlertTriangle className="w-6 h-6 text-gold" />
                    Mistakes that get {form.number} rejected
                  </h2>
                  <RevealStagger className="space-y-2.5">
                    {detail.mistakes.map((m) => (
                      <RevealItem key={m} className="flex gap-3 rounded border border-border bg-secondary/30 p-4">
                        <AlertTriangle className="w-4 h-4 text-gold shrink-0 mt-1" />
                        <span className="text-foreground/85 leading-relaxed">{m}</span>
                      </RevealItem>
                    ))}
                  </RevealStagger>
                </Reveal>

                {detail.relatedForms && detail.relatedForms.length > 0 && (
                  <Reveal>
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy mt-12 mb-5 leading-tight">
                      Related IRS forms
                    </h2>
                    <ul className="grid sm:grid-cols-2 gap-3">
                      {detail.relatedForms.map((r) => (
                        <li key={r.id}>
                          <Link
                            to={`/resources/irs-forms/${r.id}`}
                            className="block rounded border border-border bg-white hover:border-gold hover:bg-secondary/30 p-4 transition-colors"
                          >
                            <span className="font-heading font-bold text-navy">{r.label}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </Reveal>
                )}

                <Reveal>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy mt-12 mb-5 leading-tight">
                    {form.number} — frequently asked questions
                  </h2>
                  <div className="divide-y divide-border border-y border-border">
                    {detail.faqs.map((f) => (
                      <details key={f.q} className="group py-4">
                        <summary className="cursor-pointer list-none flex items-start justify-between gap-4 font-heading font-bold text-navy">
                          <span>{f.q}</span>
                          <span className="text-gold shrink-0 transition-transform group-open:rotate-45">+</span>
                        </summary>
                        <p className="mt-3 text-foreground/85 leading-relaxed">{f.a}</p>
                      </details>
                    ))}
                  </div>
                </Reveal>
              </>
            ) : (
              <Reveal>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy mb-5 leading-tight">
                  About {form.number}
                </h2>
                <dl className="space-y-5 text-[17px]">
                  <BodyRow label="Purpose" value={form.purpose} />
                  <BodyRow label="Who files" value={form.whoFiles} />
                  <BodyRow label="When used" value={form.whenUsed} />
                </dl>
                <p className="mt-8 text-sm text-muted-foreground">
                  Need help preparing {form.number}? Call {brand.phone} for a free, confidential review with a credentialed CPA.
                </p>
              </Reveal>
            )}

            {/* Call card */}
            <div className="mt-12 rounded-lg border border-border bg-gradient-navy text-white p-6 lg:p-8">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
                <FileText className="w-10 h-10 text-gold shrink-0" />
                <div className="flex-1">
                  <h3 className="font-heading text-xl font-bold mb-1">
                    Need help with {form.number}?
                  </h3>
                  <p className="text-white/80 text-sm">
                    A CPA can prepare and file {form.number} correctly the first time — and represent you through whatever follows.
                  </p>
                </div>
                <a
                  href={`tel:${brand.phoneTel}`}
                  className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-dark text-navy font-bold px-5 py-3 rounded whitespace-nowrap"
                >
                  <Phone className="w-4 h-4" /> {brand.phone}
                </a>
              </div>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2">
              <Link to="/resources/irs-forms" className="inline-flex items-center gap-1.5 text-sm font-semibold text-navy hover:text-gold">
                <ArrowLeft className="w-4 h-4" /> All IRS forms
              </Link>
              <Link to="/free-consultation" className="inline-flex items-center gap-1.5 text-sm font-semibold text-navy hover:text-gold">
                Request a free consultation <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Other forms */}
      <section className="py-12 bg-secondary/30 border-y border-border">
        <div className="container mx-auto px-4 max-w-5xl">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold mb-4">
            More IRS forms
          </p>
          <ul className="grid sm:grid-cols-2 md:grid-cols-3 gap-px bg-border">
            {irsForms
              .filter((f) => f.id !== slug)
              .slice(0, 9)
              .map((f) => (
                <li key={f.id} className="bg-white">
                  <Link
                    to={`/resources/irs-forms/${f.id}`}
                    className="block p-4 hover:bg-secondary/30 group"
                  >
                    <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-gold mb-1">
                      {f.number}
                    </p>
                    <span className="font-heading font-semibold text-navy group-hover:text-gold transition-colors text-sm flex items-center gap-1">
                      {f.title} <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100" />
                    </span>
                  </Link>
                </li>
              ))}
          </ul>
        </div>
      </section>

      <CTASection />
    </SiteLayout>
  );
};

const Row = ({ label, value }: { label: string; value: string }) => (
  <div>
    <dt className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.15em] mb-1">
      {label}
    </dt>
    <dd className="text-foreground/85 leading-relaxed">{value}</dd>
  </div>
);

const BodyRow = ({ label, value }: { label: string; value: string }) => (
  <div className="grid sm:grid-cols-[140px_1fr] gap-2 sm:gap-6">
    <dt className="text-[11px] font-bold text-muted-foreground uppercase tracking-[0.15em] pt-1">
      {label}
    </dt>
    <dd className="text-foreground/85 leading-relaxed">{value}</dd>
  </div>
);

export default IRSFormDetail;
